import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { MaskedLine } from "@/components/Reveal";

const HERO_IMG =
  "https://images.unsplash.com/photo-1604423203943-54721eff418a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxzcGFjZSUyMHRlbGVzY29wZSUyMGdhbGF4eXxlbnwwfHx8fDE3OTI2ODgxNjB8MA&ixlib=rb-4.1.0&q=85";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0.15]);

  return (
    <section
      ref={ref}
      className="relative min-h-screen flex flex-col justify-center overflow-hidden grain"
      data-testid="hero-section"
    >
      <motion.div style={{ y, opacity }} className="absolute inset-0 -z-10">
        <img
          src={HERO_IMG}
          alt=""
          aria-hidden="true"
          className="h-[115%] w-full object-cover grayscale opacity-[0.14]"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-offwhite/40 via-offwhite/60 to-offwhite" />
      </motion.div>
      <div className="absolute inset-0 -z-10 rule-lines" aria-hidden="true" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 w-full pt-32 pb-24 text-center">
        <h1
          className="mt-8 font-serif font-medium tracking-tight leading-[1.02] text-[12vw] sm:text-6xl md:text-7xl lg:text-[5.75rem]"
          data-testid="hero-headline"
        >
          <MaskedLine delay={0.25}>Influencer Marketing</MaskedLine>
          <MaskedLine delay={0.4}>
            for <em className="italic text-mutedblue">Informational</em>
          </MaskedLine>
          <MaskedLine delay={0.55}>YouTube</MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.85, ease: [0.33, 1, 0.68, 1] }}
          className="mt-8 max-w-2xl mx-auto text-base md:text-lg text-charcoal/75 leading-relaxed"
          data-testid="hero-subcopy"
        >
          Reach people who watch YouTube to understand the world. Symmori connects
          brands with creators producing insightful content across business,
          geopolitics, science, technology, education and more.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1, ease: [0.33, 1, 0.68, 1] }}
          className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-6"
        >
          <Link
            to="/contact"
            data-testid="hero-cta-primary"
            className="group inline-flex items-center gap-3 bg-ink text-white px-9 py-4 text-sm tracking-wide transition-colors duration-300 hover:bg-mutedblue"
          >
            Discuss a Campaign
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <Link
            to="/#focus"
            data-testid="hero-cta-secondary"
            className="text-sm tracking-wide text-ink underline underline-offset-8 decoration-ink/30 hover:decoration-mutedblue transition-colors duration-300"
          >
            Explore Our Focus
          </Link>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.4 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-charcoal/50"
        aria-hidden="true"
      >
        <span className="text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <ArrowDown className="h-4 w-4 animate-bounce" />
      </motion.div>
    </section>
  );
};
